import '../styles/theme.css';
import '../styles/AppBootStatus.css';
import React, { useCallback, useEffect, useState } from 'react';

import { request } from '../services/api';

/** Call /health and resolve to the resulting boot status and detail text. */
async function fetchBootStatus() {
  try {
    const body = await request('/health');

    if (body && body.status === 'healthy') {
      return { status: 'ready', detail: '' };
    }

    return { status: 'error', detail: (body && body.error) || '' };
  } catch (err) {
    return { status: 'error', detail: err && err.message ? err.message : '' };
  }
}

/** Track local-store readiness, re-checkable via the returned retry handler. */
function useBootStatus() {
  const [status, setStatus] = useState('checking');
  const [detail, setDetail] = useState('');

  const checkHealth = useCallback(async () => {
    setStatus('checking');
    const result = await fetchBootStatus();

    setStatus(result.status);
    setDetail(result.detail);
  }, []);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  return { status, detail, retry: checkHealth };
}

function BootErrorPanel({ detail, onRetry }) {
  return (
    <div className="abs-wrap">
      <div className="abs-panel" role="alert">
        <span className="abs-panel__label">Initialization failed</span>
        <h1 className="abs-panel__title">Local storage did not initialize</h1>
        <p className="abs-panel__msg">
          Brute could not set up local storage for settings, records, and run history.
          Nothing has been changed; retry once the local store is reachable.
        </p>
        {detail && <p className="abs-panel__detail">{detail}</p>}
        <button type="button" className="abs-btn" onClick={onRetry}>
          Retry
        </button>
      </div>
    </div>
  );
}

/**
 * Gate the application shell on local-store readiness.
 *
 * On mount (and on every retry), this calls the backend /health endpoint,
 * which reports whether the local storage (settings, records, run history)
 * initialized correctly on first launch. Children render only once the
 * check confirms storage is ready; otherwise an ink-bordered error panel
 * with a Retry control is shown instead of the routed UI.
 */
function AppBootStatus({ children }) {
  const { status, detail, retry } = useBootStatus();

  if (status === 'checking') {
    return null;
  }

  if (status === 'error') {
    return <BootErrorPanel detail={detail} onRetry={retry} />;
  }

  return children;
}

export default AppBootStatus;
